import React, { useState } from 'react';
import { Calendar, List, Plus, Upload, Filter, MoreVertical, AlertCircle } from 'lucide-react';
import { SHOWTIMES_DATA, SCREENS_DATA } from '../data/mockData';
export function ShowtimeManagement() {
  const [view, setView] = useState<'timeline' | 'list'>('timeline');
  // Group showtimes by screen for timeline view
  const screensWithShows = SCREENS_DATA.map(screen => ({
    ...screen,
    shows: SHOWTIMES_DATA.filter(show => show.screen === screen.name)
  }));
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SCHEDULED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'HOUSEFULL':
        return 'bg-accent/10 text-accent border-accent/20';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  return <div className="flex flex-col h-[calc(100vh-8rem)]" data-id="element-658">
      {/* Header & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-659">
        <div className="flex items-center bg-white rounded-lg p-1 border border-gray-200 soft-shadow w-fit" data-id="element-660">
          <button onClick={() => setView('timeline')} className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'timeline' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-661">
            <Calendar className="w-4 h-4" data-id="element-662" />
            Timeline
          </button>
          <button onClick={() => setView('list')} className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-663">
            <List className="w-4 h-4" data-id="element-664" />
            List View
          </button>
        </div>

        <div className="flex items-center gap-3" data-id="element-665">
          <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors soft-shadow" data-id="element-666">
            <Upload className="w-4 h-4" data-id="element-667" />
            Bulk Import CSV
          </button>
          <button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity soft-shadow" style={{
          backgroundColor: 'var(--accent-color)'
        }} data-id="element-668">
            <Plus className="w-4 h-4" data-id="element-669" />
            Add Showtime
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 bg-white rounded-xl border border-gray-200 soft-shadow overflow-hidden flex flex-col" data-id="element-670">
        {view === 'timeline' ? <div className="flex-1 overflow-auto flex flex-col" data-id="element-671">
            {/* Timeline Header (Hours) */}
            <div className="flex border-b border-gray-200 bg-gray-50 min-w-[800px]" data-id="element-672">
              <div className="w-48 flex-shrink-0 p-4 border-r border-gray-200 font-semibold text-gray-700 text-sm" data-id="element-673">
                Screens
              </div>
              <div className="flex-1 flex relative h-12" data-id="element-674">
                {/* Mocking hours 9AM to 11PM */}
                {Array.from({
              length: 15
            }).map((_, i) => <div key={i} className="flex-1 border-r border-gray-200/50 relative" data-id="element-675">
                    <span className="absolute -left-3 top-3 text-xs text-gray-400 font-medium" data-id="element-676">
                      {i + 9}:00
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Timeline Rows */}
            <div className="flex-1 overflow-y-auto min-w-[800px]" data-id="element-677">
              {screensWithShows.map(screen => <div key={screen.id} className="flex border-b border-gray-100 hover:bg-gray-50/50 transition-colors group" data-id="element-678">
                  <div className="w-48 flex-shrink-0 p-4 border-r border-gray-200 bg-white group-hover:bg-gray-50/50 transition-colors" data-id="element-679">
                    <div className="font-semibold text-gray-900 text-sm" data-id="element-680">
                      {screen.name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-681">
                      {screen.type} • {screen.capacity} seats
                    </div>
                  </div>
                  <div className="flex-1 relative h-20 py-2" data-id="element-682">
                    {/* Grid lines */}
                    <div className="absolute inset-0 flex pointer-events-none" data-id="element-683">
                      {Array.from({
                  length: 15
                }).map((_, i) => <div key={i} className="flex-1 border-r border-gray-100" data-id="element-684"></div>)}
                    </div>

                    {/* Shows */}
                    {screen.shows.map(show => {
                // Calculate rough position based on 9AM start, 15 hours total
                const startHour = parseInt(show.startTime.split(':')[0]);
                const startMin = parseInt(show.startTime.split(':')[1]);
                const endHour = parseInt(show.endTime.split(':')[0]);
                const endMin = parseInt(show.endTime.split(':')[1]);
                const startOffset = (startHour - 9 + startMin / 60) / 15 * 100;
                const duration = (endHour - startHour + (endMin - startMin) / 60) / 15 * 100;
                return <div key={show.id} className={`absolute top-2 bottom-2 rounded-md border p-2 overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${getStatusColor(show.status)}`} style={{
                  left: `${startOffset}%`,
                  width: `${duration}%`,
                  ...(show.status === 'HOUSEFULL' ? {
                    color: 'var(--accent-color)',
                    borderColor: 'color-mix(in srgb, var(--accent-color) 30%, transparent)',
                    backgroundColor: 'color-mix(in srgb, var(--accent-color) 10%, white)'
                  } : {})
                }} data-id="element-685">
                          <div className="font-bold text-xs truncate" data-id="element-686">
                            {show.movie}
                          </div>
                          <div className="text-[10px] opacity-80 mt-0.5 flex items-center justify-between" data-id="element-687">
                            <span data-id="element-688">
                              {show.startTime} - {show.endTime}
                            </span>
                            <span className="font-semibold" data-id="element-689">
                              {show.occupancy}%
                            </span>
                          </div>
                        </div>;
              })}
                  </div>
                </div>)}
            </div>
          </div> : <div className="flex-1 flex flex-col" data-id="element-690">
            {/* List Filters */}
            <div className="p-4 border-b border-gray-200 flex items-center gap-4 bg-gray-50" data-id="element-691">
              <div className="flex items-center gap-2 text-sm text-gray-600 font-medium" data-id="element-692">
                <Filter className="w-4 h-4" data-id="element-693" /> Filters:
              </div>
              <select className="text-sm border-gray-300 rounded-md py-1.5 pl-3 pr-8" data-id="element-694">
                <option data-id="element-695">All Screens</option>
                {SCREENS_DATA.map(s => <option key={s.id} data-id="element-696">{s.name}</option>)}
              </select>
              <select className="text-sm border-gray-300 rounded-md py-1.5 pl-3 pr-8" data-id="element-697">
                <option data-id="element-698">All Statuses</option>
                <option data-id="element-699">SCHEDULED</option>
                <option data-id="element-700">HOUSEFULL</option>
                <option data-id="element-701">CANCELLED</option>
              </select>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto" data-id="element-702">
              <table className="w-full text-left border-collapse" data-id="element-703">
                <thead data-id="element-704">
                  <tr className="bg-white border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500" data-id="element-705">
                    <th className="p-4 font-semibold" data-id="element-706">Movie</th>
                    <th className="p-4 font-semibold" data-id="element-707">Screen & Format</th>
                    <th className="p-4 font-semibold" data-id="element-708">Time</th>
                    <th className="p-4 font-semibold" data-id="element-709">Occupancy</th>
                    <th className="p-4 font-semibold" data-id="element-710">Status</th>
                    <th className="p-4 font-semibold text-right" data-id="element-711">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-712">
                  {SHOWTIMES_DATA.map(show => <tr key={show.id} className="hover:bg-gray-50 transition-colors" data-id="element-713">
                      <td className="p-4" data-id="element-714">
                        <div className="font-bold text-gray-900" data-id="element-715">
                          {show.movie}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-716">
                          {show.language}
                        </div>
                      </td>
                      <td className="p-4" data-id="element-717">
                        <div className="font-medium text-gray-900" data-id="element-718">
                          {show.screen}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-719">
                          {show.format}
                        </div>
                      </td>
                      <td className="p-4" data-id="element-720">
                        <div className="font-medium text-gray-900" data-id="element-721">
                          {show.date}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-722">
                          {show.startTime} - {show.endTime}
                        </div>
                      </td>
                      <td className="p-4" data-id="element-723">
                        <div className="flex items-center gap-2" data-id="element-724">
                          <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-725">
                            <div className="h-full rounded-full" style={{
                        width: `${show.occupancy}%`,
                        backgroundColor: show.occupancy > 90 ? 'var(--accent-color)' : '#10B981'
                      }} data-id="element-726"></div>
                          </div>
                          <span className="text-xs font-medium text-gray-700" data-id="element-727">
                            {show.occupancy}%
                          </span>
                        </div>
                      </td>
                      <td className="p-4" data-id="element-728">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${getStatusColor(show.status)}`} style={show.status === 'HOUSEFULL' ? {
                    color: 'var(--accent-color)',
                    borderColor: 'color-mix(in srgb, var(--accent-color) 30%, transparent)',
                    backgroundColor: 'color-mix(in srgb, var(--accent-color) 10%, white)'
                  } : {}} data-id="element-729">
                          {show.status}
                        </span>
                      </td>
                      <td className="p-4 text-right" data-id="element-730">
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" data-id="element-731">
                          <MoreVertical className="w-4 h-4" data-id="element-732" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}